import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import SectionHeader from "../SectionHeader";
import { useTranslation } from "react-i18next";

interface StoreButton {
    name: string; // کلید داخلی برای شناسایی
    storeNameKey: string; // کلید ترجمه نام فروشگاه
    icon: React.ReactNode;
    gradient: string;
    tooltipKey?: string; // کلید ترجمه تولتیپ
    url: string;
    badgeKey?: string; // کلید ترجمه نشان (مثل "پیشنهادی")
}

const ModernAppStoreButtons: React.FC = () => {
    const { t, i18n } = useTranslation("ModernAppStoreButtons");

    const [activeTab, setActiveTab] = useState<"ios" | "android">("ios");
    const [hoveredButton, setHoveredButton] = useState<number | null>(null);
    const [showQRModal, setShowQRModal] = useState(false);

    const currentLang = i18n.language;
    const isRtl = ["fa", "ar"].includes(currentLang);
    const dir = isRtl ? "rtl" : "ltr";

    // تعریف دکمه‌ها (بدون متن سخت‌کد شده، با استفاده از کلیدهای JSON)
    const iosButtons: StoreButton[] = [
        {
            name: "Sibche",
            storeNameKey: "stores.ios.0.storeName",
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="12" fill="currentColor" />
                    <path d="M12 6C8.7 6 6 8.7 6 12s2.7 6 6 6 6-2.7 6-6-2.7-6-6-6zm0 10c-2.2 0-4-1.8-4-4s1.8-4 4-4 4 1.8 4 4-1.8 4-4 4z" fill="#FFFFFF" />
                    <path d="M13 9l-2 4h3l-4 4 1-4H8l5-4z" fill="#FFFFFF" />
                </svg>
            ),
            gradient: "from-[#0072CE] to-[#00E5FF]",
            tooltipKey: "stores.ios.0.tooltip",
            url: "https://sibche.com/",
            badgeKey: "stores.ios.0.badge",
        },
        {
            name: "AnarDoni",
            storeNameKey: "stores.ios.1.storeName",
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" />
                    <path d="M12 6c-3.3 0-6 2.7-6 6s2.7 6 6 6 6-2.7 6-6-2.7-6-6-6zm0 10c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z" />
                    <circle cx="12" cy="12" r="2.5" />
                </svg>
            ),
            gradient: "from-[#0072CE] to-[#00A5FF]",
            tooltipKey: "stores.ios.1.tooltip",
            url: "https://anardoni.com/ios/app/anardoniexportayandapdelband",
        },
        {
            name: "Web App",
            storeNameKey: "stores.ios.2.storeName",
            icon: <img src="./media/pwa-logo.png" alt="PWA" className="w-8 h-8 object-contain" />,
            gradient: "from-[#0072CE] to-[#2E82EF]",
            tooltipKey: "stores.ios.2.tooltip",
            url: "https://app.delbandcare.com/",
        },
        {
            name: "SibIrani",
            storeNameKey: "stores.ios.3.storeName",
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" />
                    <path d="M12 6c-3.3 0-6 2.7-6 6s2.7 6 6 6 6-2.7 6-6-2.7-6-6-6zm0 10c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z" />
                    <circle cx="12" cy="12" r="2.5" />
                </svg>
            ),
            gradient: "from-[#0072CE] to-[#00A5FF]",
            tooltipKey: "stores.ios.3.tooltip",
            url: "https://sibirani.com/apps/DelBand/",
        },
    ];

    const androidButtons: StoreButton[] = [
        {
            name: "Bazaar",
            storeNameKey: "stores.android.0.storeName",
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C5.37 0 0 5.37 0 12s5.37 12 12 12 12-5.37 12-12S18.63 0 12 0zm-.47 13.5H8.53c-.42 0-.75-.33-.75-.75s.33-.75.75-.75h3c.42 0 .75.33.75.75s-.33.75-.75.75zm3.94 0h-2.47c-.42 0-.75-.33-.75-.75s.33-.75.75-.75h2.47c.42 0 .75.33.75.75s-.33.75-.75.75zM8.53 10.5c-.42 0-.75-.33-.75-.75s.33-.75.75-.75h6.94c.42 0 .75.33.75.75s-.33.75-.75.75H8.53z" />
                </svg>
            ),
            gradient: "from-[#0072CE] to-[#00E5FF]",
            tooltipKey: "stores.android.0.tooltip",
            url: "https://cafebazaar.ir/",
        },
        {
            name: "Myket",
            storeNameKey: "stores.android.1.storeName",
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" />
                </svg>
            ),
            gradient: "from-[#0072CE] to-[#00A5FF]",
            tooltipKey: "stores.android.1.tooltip",
            url: "https://myket.ir/",
        },
        {
            name: "Direct Download",
            storeNameKey: "stores.android.2.storeName",
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="7 10 12 15 17 10"></polyline>
                    <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
            ),
            gradient: "from-[#0072CE] to-[#2E82EF]",
            url: "https://service.delbandcare.com/api/latest-release/?direct=true",
        },
    ];

    return (
        <div className="w-full relative overflow-hidden py-4" dir={dir}>
            {/* Background Elements */}
            <div className="absolute inset-0 -z-10">
                <div className="absolute top-0 right-0 w-full h-full"></div>
                <div className="absolute bottom-0 left-0 w-full h-full "></div>
                <div className="absolute -top-24 -right-24 w-96 h-96  rounded-full blur-3xl"></div>
                <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full blur-3xl"></div>
                <div className="absolute inset-0 bg-[url('/noise.png')] opacity-[0.02] mix-blend-overlay"></div>
            </div>

            <div className="max-w-5xl mx-auto relative">
                {/* Header */}
                <div className="text-center mb-14">
                    <motion.div className="inline-block mb-8" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}>
                        <div className="w-20 h-20 bg-gradient-to-br from-[#0038A8] to-[#0072CE] rounded-2xl shadow-lg shadow-[#0072CE]/20 flex items-center justify-center text-white text-3xl font-bold transform -rotate-6">
                            {/* Logo SVG (kept as is) */}
                            <img src="media/Logo1.svg" alt={t("altTexts.logo")} className="w-12 h-12 object-contain" />
                        </div>
                    </motion.div>

                    <SectionHeader
                        regularText={t("sectionHeader.regularText")}
                        highlightedText={t("sectionHeader.highlightedText")}
                        descriptionHighlight={t("sectionHeader.descriptionHighlight")}
                        descriptionText={t("sectionHeader.descriptionText")}
                    />
                </div>

                {/* Platform Selector */}
                <motion.div className="flex justify-center mb-12" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}>
                    <div className="bg-white/90 backdrop-blur-md p-1.5 rounded-full shadow-xl border border-[#E0F3FF] flex relative overflow-hidden hover:shadow-[0_8px_30px_rgb(0,114,206,0.12)] transition-all duration-300">
                        <div className="absolute inset-0 bg-gradient-to-r from-[#F5F9FF] via-white to-[#F5F9FF] opacity-80"></div>

                        {/* iOS Tab */}
                        <button
                            className={`relative px-6 sm:px-10 py-3 sm:py-3.5 rounded-full text-sm font-medium transition-all duration-300 ${activeTab === "ios" ? "text-white" : "text-[#64748B] hover:text-[#0072CE]"
                                }`}
                            onClick={() => setActiveTab("ios")}
                            aria-pressed={activeTab === "ios"}
                        >
                            <span className="relative z-10 flex items-center gap-2.5 font-semibold">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 384 512" fill="currentColor">
                                    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
                                </svg>
                                <span className="tracking-wide">{t("tabs.ios")}</span>
                            </span>
                            {activeTab === "ios" && (
                                <motion.div
                                    className="absolute inset-0 bg-gradient-to-r from-[#0038A8] to-[#0072CE] rounded-full shadow-md"
                                    layoutId="activeTabBackground"
                                    initial={{ opacity: 0.9 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                                />
                            )}
                        </button>

                        {/* Android Tab */}
                        <button
                            className={`relative px-6 sm:px-10 py-3 sm:py-3.5 rounded-full text-sm font-medium transition-all duration-300 ${activeTab === "android" ? "text-white" : "text-[#64748B] hover:text-[#0072CE]"
                                }`}
                            onClick={() => setActiveTab("android")}
                            aria-pressed={activeTab === "android"}
                        >
                            <span className="relative z-10 flex items-center gap-2.5 font-semibold">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M6 18c0 .55.45 1 1 1h1v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h2v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h1c.55 0 1-.45 1-1V8H6v10zM3.5 8C2.67 8 2 8.67 2 9.5v7c0 .83.67 1.5 1.5 1.5S5 17.33 5 16.5v-7C5 8.67 4.33 8 3.5 8zm17 0c-.83 0-1.5.67-1.5 1.5v7c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5v-7c0-.83-.67-1.5-1.5-1.5zm-4.97-5.84l1.3-1.3c.2-.2.2-.51 0-.71-.2-.2-.51-.2-.71 0l-1.48 1.48C13.85 1.23 12.95 1 12 1c-.96 0-1.86.23-2.66.63L7.85.15c-.2-.2-.51-.2-.71 0-.2.2-.2.51 0 .71l1.31 1.31C6.97 3.26 6 5.01 6 7h12c0-1.99-.97-3.75-2.47-4.84zM10 5H9V4h1v1zm5 0h-1V4h1v1z" />
                                </svg>
                                <span className="tracking-wide">{t("tabs.android")}</span>
                            </span>
                            {activeTab === "android" && (
                                <motion.div
                                    className="absolute inset-0 bg-gradient-to-r from-[#0038A8] to-[#0072CE] rounded-full shadow-md"
                                    layoutId="activeTabBackground"
                                    initial={{ opacity: 0.9 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                                />
                            )}
                        </button>

                        {/* Decorative blobs */}
                        <motion.div
                            className="absolute -top-6 -right-6 w-12 h-12 bg-[#EBF5FF] rounded-full opacity-50"
                            animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.7, 0.5] }}
                            transition={{ duration: 4, repeat: Infinity, repeatType: "reverse" }}
                        ></motion.div>
                        <motion.div
                            className="absolute -bottom-6 -left-6 w-12 h-12 bg-[#ffeee0] rounded-full opacity-50"
                            animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.7, 0.5] }}
                            transition={{ duration: 4, repeat: Infinity, repeatType: "reverse", delay: 2 }}
                        ></motion.div>
                    </div>
                </motion.div>

                {/* App Store Buttons */}
                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeTab}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.4 }}
                        className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-[#E0F3FF] max-w-xl mx-auto relative overflow-hidden"
                    >
                        {/* Background pattern */}
                        <div className="absolute inset-0 opacity-5">
                            <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(#0072CE 1px, transparent 1px)", backgroundSize: "20px 20px" }}></div>
                        </div>

                        {/* Top Row - Two buttons side by side */}
                        <div className="flex flex-col sm:flex-row gap-4 mb-4 relative z-10">
                            {(activeTab === "ios" ? iosButtons : androidButtons).slice(0, 2).map((button, index) => (
                                <motion.a
                                    key={button.name}
                                    href={activeTab === "android" ? "#" : button.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`relative flex items-center justify-center ${activeTab === "android" ? "bg-white/10 backdrop-blur-sm cursor-not-allowed" : "bg-white"
                                        } rounded-xl border border-[#E0F3FF] p-3 flex-1 hover:shadow-lg transition-all duration-300 group overflow-hidden ${index === 0 ? "ring-2 ring-[#0072CE]/10" : ""}`}
                                    onMouseEnter={() => setHoveredButton(index)}
                                    onMouseLeave={() => setHoveredButton(null)}
                                    whileHover={activeTab === "android" ? {} : { y: -4, boxShadow: "0 10px 25px rgba(0, 114, 206, 0.2)" }}
                                    whileTap={activeTab === "android" ? {} : { scale: 0.98 }}
                                    initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.4, delay: index * 0.1 }}
                                    onClick={activeTab === "android" ? (e) => e.preventDefault() : undefined}
                                >
                                    <motion.div
                                        className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent ${activeTab === "android" ? "opacity-0" : "opacity-0 group-hover:opacity-100"
                                            }`}
                                        initial={{ x: "-100%" }}
                                        whileHover={activeTab === "android" ? {} : { x: "100%", transition: { repeat: Infinity, repeatType: "loop", duration: 1.5, ease: "linear" } }}
                                    />

                                    {/* Store Banner Image */}
                                    <div className="relative w-full h-16 sm:h-20 flex items-center justify-center">
                                        <img
                                            src={`./media/${activeTab === "ios"
                                                    ? index === 0
                                                        ? "Sibche-Badge-2-01.webp"
                                                        : "anardoni-badge-dark-persian-C6JGcFZw.webp"
                                                    : index === 0
                                                        ? "bazar-Badge-2-01.webp"
                                                        : "myket-Badge-2-01.webp"
                                                }`}
                                            alt={t(button.storeNameKey)}
                                            className={`w-full h-full transition-transform duration-300 max-w-[218px] ${activeTab === "android" ? "opacity-50" : "group-hover:scale-105"}`}
                                        />

                                        <div className="absolute inset-0 bg-white/0 group-hover:bg-white/10 rounded-xl transition-all duration-300"></div>

                                        <div
                                            className={`absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-10 h-1 ${activeTab === "android" ? "bg-[#0072CE]/0" : "bg-[#0072CE]/0 group-hover:bg-[#0072CE]/70"
                                                } rounded-full transition-all duration-300`}
                                        ></div>

                                        {/* "Coming Soon" for Android */}
                                        {activeTab === "android" && (
                                            <div className="absolute inset-0 flex items-center justify-center bg-black/30 backdrop-blur-sm rounded-xl">
                                                <div className="bg-white/20 backdrop-blur-md px-4 py-2 rounded-full border border-white/30 shadow-lg">
                                                    <span className="text-white font-bold text-sm">{t("comingSoon")}</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Recommended Badge */}
                                    {button.badgeKey && activeTab === "ios" && (
                                        <div
                                            className={`absolute top-0 ${isRtl ? "left-0 rounded-br-md" : "right-0 rounded-bl-md"
                                                } bg-gradient-to-r from-[#0072CE] to-[#00E5FF] text-white text-xs py-1 px-2`}
                                        >
                                            {t(button.badgeKey)}
                                        </div>
                                    )}

                                    {/* Tooltip */}
                                    {button.tooltipKey && hoveredButton === index && (
                                        <motion.div
                                            className="absolute -top-12 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded-lg bg-gray-800 text-white text-xs whitespace-nowrap z-20 shadow-lg"
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: 10 }}
                                        >
                                            {activeTab === "android" ? t("comingSoonTooltip") : t(button.tooltipKey)}
                                            <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 rotate-45 bg-gray-800"></div>
                                        </motion.div>
                                    )}
                                </motion.a>
                            ))}
                        </div>

                        {/* Bottom Row */}
                        <div className="flex flex-col sm:flex-row gap-4 mb-4 relative z-10">
                            {activeTab === "ios"
                                ? // iOS Bottom Buttons
                                iosButtons.slice(2, 4).map((button, index) => (
                                    <motion.a
                                        key={button.name}
                                        href={button.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="relative flex items-center justify-center bg-white rounded-xl border border-[#E0F3FF] p-3 flex-1 hover:shadow-lg transition-all duration-300 group overflow-hidden"
                                        onMouseEnter={() => setHoveredButton(index + 2)}
                                        onMouseLeave={() => setHoveredButton(null)}
                                        whileHover={{ y: -4, boxShadow: "0 10px 25px rgba(0, 114, 206, 0.2)" }}
                                        whileTap={{ scale: 0.98 }}
                                        initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.4, delay: index * 0.1 }}
                                    >
                                        <motion.div
                                            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100"
                                            initial={{ x: "-100%" }}
                                            whileHover={{ x: "100%", transition: { repeat: Infinity, repeatType: "loop", duration: 1.5, ease: "linear" } }}
                                        />
                                        <div className="relative h-16 sm:h-20 flex items-center justify-center w-[218px]">
                                            <img
                                                src={`./media/${index === 0 ? "pwa-Badge-2-01.webp" : "Sibirani-Badge-2-01.webp"}`}
                                                alt={t(button.storeNameKey)}
                                                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 rounded-lg"
                                            />
                                            <div className="absolute inset-0 bg-white/0 group-hover:bg-white/10 rounded-xl transition-all duration-300"></div>
                                            <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-10 h-1 bg-[#0072CE]/0 group-hover:bg-[#0072CE]/70 rounded-full transition-all duration-300"></div>
                                        </div>
                                        {button.tooltipKey && hoveredButton === index + 2 && (
                                            <motion.div
                                                className="absolute -top-12 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded-lg bg-gray-800 text-white text-xs whitespace-nowrap z-20 shadow-lg"
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: 10 }}
                                            >
                                                {t(button.tooltipKey)}
                                                <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 rotate-45 bg-gray-800"></div>
                                            </motion.div>
                                        )}
                                    </motion.a>
                                ))
                                : null}
                        </div>

                        {/* Android Direct Download (Full Width) */}
                        {activeTab === "android" && (
                            <motion.a
                                href={androidButtons[2].url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="relative flex items-center justify-center bg-white rounded-xl border border-[#E0F3FF] p-3 w-full hover:shadow-lg transition-all duration-300 group overflow-hidden"
                                onMouseEnter={() => setHoveredButton(2)}
                                onMouseLeave={() => setHoveredButton(null)}
                                whileHover={{ y: -4, boxShadow: "0 10px 25px rgba(0, 114, 206, 0.2)" }}
                                whileTap={{ scale: 0.98 }}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: 0.2 }}
                            >
                                <motion.div
                                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100"
                                    initial={{ x: "-100%" }}
                                    whileHover={{ x: "100%", transition: { repeat: Infinity, repeatType: "loop", duration: 1.5, ease: "linear" } }}
                                />
                                <div className="relative w-full h-16 sm:h-20 flex items-center justify-center">
                                    <div className="bg-gradient-to-l from-[#0077DF] to-[#309EFD] rounded-lg w-[98%] h-[90%] flex items-center justify-between px-4">
                                        <div className="bg-white p-2 rounded-lg">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="#309EFD">
                                                <path d="M6 18c0 .55.45 1 1 1h1v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h2v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h1c.55 0 1-.45 1-1V8H6v10zM3.5 8C2.67 8 2 8.67 2 9.5v7c0 .83.67 1.5 1.5 1.5S5 17.33 5 16.5v-7C5 8.67 4.33 8 3.5 8zm17 0c-.83 0-1.5.67-1.5 1.5v7c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5v-7c0-.83-.67-1.5-1.5-1.5zm-4.97-5.84l1.3-1.3c.2-.2.2-.51 0-.71-.2-.2-.51-.2-.71 0l-1.48 1.48C13.85 1.23 12.95 1 12 1c-.96 0-1.86.23-2.66.63L7.85.15c-.2-.2-.51-.2-.71 0-.2.2-.2.51 0 .71l1.31 1.31C6.97 3.26 6 5.01 6 7h12c0-1.99-.97-3.75-2.47-4.84zM10 5H9V4h1v1zm5 0h-1V4h1v1z" />
                                            </svg>
                                        </div>
                                        <div className={`flex flex-col text-white ${isRtl ? "items-start" : "items-end"}`}>
                                            <span className="text-sm mb-1">{t("stores.android.2.downloadText")}</span>
                                            <span className="font-semibold">{t("stores.android.2.versionText")}</span>
                                        </div>
                                    </div>
                                </div>
                                {androidButtons[2].tooltipKey && hoveredButton === 2 && (
                                    <motion.div
                                        className="absolute -top-12 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded-lg bg-gray-800 text-white text-xs whitespace-nowrap z-20 shadow-lg"
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 10 }}
                                    >
                                        {t(androidButtons[2].tooltipKey)}
                                        <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 rotate-45 bg-gray-800"></div>
                                    </motion.div>
                                )}
                            </motion.a>
                        )}
                    </motion.div>
                </AnimatePresence>

                {/* QR Code Link */}
                <motion.div
                    className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 text-center"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.6 }}
                >
                    <motion.a
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            setShowQRModal(true);
                        }}
                        className="inline-flex items-center gap-2 text-sm font-medium px-5 py-2.5 rounded-full bg-white border border-gray-100 shadow-sm transition-all duration-300 text-[#0077DF] hover:bg-[#4A6CF7]/5"
                        whileHover={{ y: -2, boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}
                        whileTap={{ scale: 0.98 }}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                            <rect x="7" y="7" width="3" height="3"></rect>
                            <rect x="14" y="7" width="3" height="3"></rect>
                            <rect x="7" y="14" width="3" height="3"></rect>
                            <rect x="14" y="14" width="3" height="3"></rect>
                        </svg>
                        {t("qrCode.scanButton")}
                    </motion.a>

                    <span className="text-[#64748B] text-sm">{t("qrCode.scanDescription")}</span>
                </motion.div>

                {/* QR Code Modal */}
                <AnimatePresence>
                    {showQRModal && (
                        <motion.div
                            className="fixed inset-0 bg-[#1A202C]/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setShowQRModal(false)}
                        >
                            <motion.div
                                className="bg-white rounded-[24px] p-8 max-w-md w-full relative shadow-xl border border-gray-100"
                                dir={dir}
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.9, opacity: 0 }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <button className={`absolute top-4 ${isRtl ? "left-4" : "right-4"} text-[#64748B] hover:text-[#1A202C] transition-colors`} onClick={() => setShowQRModal(false)}>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <line x1="18" y1="6" x2="6" y2="18"></line>
                                        <line x1="6" y1="6" x2="18" y2="18"></line>
                                    </svg>
                                </button>

                                <h3 className="text-xl font-bold text-center mb-6 text-[#1A202C]">{t("qrCode.modalTitle")}</h3>

                                <motion.div className="flex flex-col items-center">
                                    <div className="w-48 h-48 bg-gray-100 rounded-xl p-3 mb-6 shadow-inner flex items-center justify-center">
                                        <QRCodeSVG value={window.location.href} size={160} level="H" fgColor="#1A202C" bgColor="#ffffff" includeMargin={false} />
                                    </div>

                                    <p className="text-[#64748B] text-center text-sm mb-6">{t("qrCode.modalDescription")}</p>

                                    <div className="flex gap-4">
                                        <motion.button
                                            className="px-5 py-2.5 rounded-full text-sm font-medium bg-[#4A6CF7] text-white hover:bg-[#4A6CF7]/90 transition-colors"
                                            onClick={() => setShowQRModal(false)}
                                            whileHover={{ y: -2 }}
                                            whileTap={{ scale: 0.98 }}
                                        >
                                            {t("qrCode.closeButton")}
                                        </motion.button>
                                        <motion.a
                                            href="#"
                                            className="px-5 py-2.5 rounded-full text-sm font-medium bg-gray-100 text-[#1A202C] hover:bg-gray-200 transition-colors"
                                            whileHover={{ y: -2 }}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                navigator.clipboard.writeText(window.location.href);
                                                alert(t("qrCode.copySuccess"));
                                            }}
                                        >
                                            {t("qrCode.copyButton")}
                                        </motion.a>
                                    </div>
                                </motion.div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default ModernAppStoreButtons;
